import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';

// ---- P1-B customer portal (staff side) ----
export type PortalContactStatus = 'INVITED' | 'ACTIVE' | 'DISABLED';

export interface PortalSettings {
  institutionId: string; serverConfigured: boolean; enabled: boolean; emailEnabled: boolean;
  uploadsEnabled: boolean; sessionMinutes: number;
}
export interface PortalSettingsRequest { enabled: boolean; emailEnabled: boolean; uploadsEnabled: boolean; sessionMinutes: number; }

export interface PortalContact {
  id: string; customerId: string; name: string; email: string; status: PortalContactStatus;
  lastLoginAt?: string | null; lockedUntil?: string | null; disabledReason?: string | null; createdAt?: string;
}
export interface OneTimeLink { contact: PortalContact; url: string; expiresAt: string; emailed: boolean; emailedTo?: string | null; }

export interface PortalActivity {
  id: string; occurredAt: string; event: string; contactId?: string | null; contactName?: string | null;
  documentType?: string | null; documentId?: string | null; ip?: string | null; detail?: string | null; actorUserId?: string | null;
}
export interface PortalActivityPage { rows: PortalActivity[]; total: number; page: number; size: number; }
export interface PortalActivityFilter { contactId?: string; event?: string; from?: string; to?: string; }

export interface PortalUpload {
  id: string; contactId: string; contactName?: string | null; salesInvoiceId?: string | null; originalName: string;
  contentType: string; sizeBytes: number; note?: string | null; status: 'NEW' | 'REVIEWED'; uploadedAt: string; reviewedAt?: string | null;
}

const clean = (f: PortalActivityFilter) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

export const PortalAdminService = {
  getSettings: async (institutionId: string): Promise<PortalSettings> =>
    (await axios.get(ENDPOINTS.PORTAL_ADMIN.SETTINGS(institutionId))).data,
  saveSettings: async (institutionId: string, req: PortalSettingsRequest): Promise<PortalSettings> =>
    (await axios.put(ENDPOINTS.PORTAL_ADMIN.SETTINGS(institutionId), req)).data,
  contacts: async (institutionId: string, customerId: string): Promise<PortalContact[]> =>
    (await axios.get(ENDPOINTS.PORTAL_ADMIN.CONTACTS(institutionId, customerId))).data,
  invite: async (institutionId: string, customerId: string, body: { name: string; email: string }): Promise<OneTimeLink> =>
    (await axios.post(ENDPOINTS.PORTAL_ADMIN.CONTACTS(institutionId, customerId), body)).data,
  reinvite: async (institutionId: string, contactId: string): Promise<OneTimeLink> =>
    (await axios.post(ENDPOINTS.PORTAL_ADMIN.REINVITE(institutionId, contactId))).data,
  resetLink: async (institutionId: string, contactId: string): Promise<OneTimeLink> =>
    (await axios.post(ENDPOINTS.PORTAL_ADMIN.RESET_LINK(institutionId, contactId))).data,
  unlock: async (institutionId: string, contactId: string): Promise<PortalContact> =>
    (await axios.post(ENDPOINTS.PORTAL_ADMIN.UNLOCK(institutionId, contactId))).data,
  deactivate: async (institutionId: string, contactId: string, reason: string): Promise<PortalContact> =>
    (await axios.post(ENDPOINTS.PORTAL_ADMIN.DEACTIVATE(institutionId, contactId), { reason })).data,
  reactivate: async (institutionId: string, contactId: string): Promise<PortalContact> =>
    (await axios.post(ENDPOINTS.PORTAL_ADMIN.REACTIVATE(institutionId, contactId))).data,
  activity: async (institutionId: string, customerId: string, f: PortalActivityFilter, page = 0, size = 50): Promise<PortalActivityPage> =>
    (await axios.get(ENDPOINTS.PORTAL_ADMIN.ACTIVITY(institutionId, customerId), { params: { ...clean(f), page, size } })).data,
  exportActivity: async (institutionId: string, customerId: string, f: PortalActivityFilter): Promise<Blob> =>
    (await axios.get(ENDPOINTS.PORTAL_ADMIN.ACTIVITY_EXPORT(institutionId, customerId), { params: clean(f), responseType: 'blob' })).data,
  uploads: async (institutionId: string, customerId: string): Promise<PortalUpload[]> =>
    (await axios.get(ENDPOINTS.PORTAL_ADMIN.UPLOADS(institutionId, customerId))).data,
  downloadUpload: async (institutionId: string, uploadId: string): Promise<Blob> =>
    (await axios.get(ENDPOINTS.PORTAL_ADMIN.UPLOAD_DOWNLOAD(institutionId, uploadId), { responseType: 'blob' })).data,
  reviewUpload: async (institutionId: string, uploadId: string): Promise<PortalUpload> =>
    (await axios.post(ENDPOINTS.PORTAL_ADMIN.UPLOAD_REVIEW(institutionId, uploadId))).data,
};
// ---- end P1-B ----
